import { useCallback, useEffect, useMemo } from 'react';

import type { ApolloError } from '@apollo/client';
import { useLazyQuery } from '@apollo/client';

import type { UpdateChatHistory } from 'bundles/ai-coach-platform/components/patterns/chat/store/actions/types';
import { updateChatHistory } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type {
  AiCoachPlatform_ChatMessagesPaginatedResultQueryVariables as AiCoachChatMessagesPaginatedResultQueryVariables,
  AiCoachPlatform_ChatMessagesPaginatedResultQuery as ChatMessagesPaginatedResultQuery,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachChatMessagesByChatSessionId';
import aiCoachChatMessagesByChatSessionIdQuery from 'bundles/ai-coach-platform/queries/aiCoachChatMessagesByChatSessionId.graphql';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';

import { MESSAGE_SENDER } from './constants';

type Props = {
  historyLimit: number;
  chatSessionId?: string;
};

/**
 * Parse the fetched result and update the chat history
 * @param data the data fetched from the query
 * @param error the apollo error happened during fetching
 */
const parseFetchedResult = (
  data: ChatMessagesPaginatedResultQuery | undefined,
  error: ApolloError | undefined
): UpdateChatHistory['messages'] => {
  if (error) {
    captureMessageInSentry({ source: 'useChatHistory.parseFetchedResult', error });
    return [];
  }

  const messages = data?.AiCoach?.queryByChatSessionId?.elements;
  if (messages) {
    const parsedMessages: UpdateChatHistory['messages'] = messages.map((message) => {
      if (message.senderType === MESSAGE_SENDER.CLIENT) {
        return {
          id: message.id,
          sender: message.senderType,
          text: message.message,
          timestamp: new Date(message.createdAt).valueOf(),
          courseraContext: message.courseraContext,
        };
      } else if (message.senderType === MESSAGE_SENDER.RESPONSE) {
        return {
          id: message.id,
          sender: message.senderType,
          text: message.message,
          timestamp: new Date(message.createdAt).valueOf(),
          /** Feedback from the user on the AI coach response via a like/dislike */
          userFeedback: message.userFeedback,
          navigationButtons: message.navigationButtonsV2,
          recommendedActions: message.recommendedActionsV2,
          courseraContext: message.courseraContext,
          recommendedEdits: message.recommendedEdits,
        };
      }
      return {
        id: message.id,
        sender: message.senderType,
        text: message.message,
        timestamp: new Date(message.createdAt).valueOf(),
        courseraContext: message.courseraContext,
        recommendedEdits: message.recommendedEdits,
      };
    });

    // TODO: MAGIC: chat history is coming in reverse order, so we make it the new messages are at the bottom of the chat by reversing it here.
    updateChatHistory(parsedMessages.reverse());
    return parsedMessages;
  }

  return [];
};

export function useChatHistory({ historyLimit, chatSessionId = '' }: Props): {
  loading: boolean;
  error: ApolloError | undefined;
  fetchChatHistory: (chatSessionId: string) => Promise<UpdateChatHistory['messages']>;
} {
  const [fetchQuery, { loading, error }] = useLazyQuery<
    ChatMessagesPaginatedResultQuery,
    AiCoachChatMessagesPaginatedResultQueryVariables
  >(aiCoachChatMessagesByChatSessionIdQuery, {
    variables: {
      chatSessionId,
      limit: historyLimit,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (error) {
      captureMessageInSentry({ source: 'useChatHistory', error });
    }
  }, [error, loading]);

  const fetchChatHistory = useCallback<(chatSessionIdToFetch: string) => Promise<UpdateChatHistory['messages']>>(
    async (chatSessionIdToFetch: string) => {
      const { data, error: fetchError } = await fetchQuery({
        variables: { chatSessionId: chatSessionIdToFetch, limit: historyLimit },
      });

      return parseFetchedResult(data, fetchError);
    },
    [fetchQuery, historyLimit]
  );

  return useMemo(
    () => ({
      loading,
      error,
      fetchChatHistory,
    }),
    [loading, error, fetchChatHistory]
  );
}
